import { useEffect } from 'react';

const SEPARATOR = ' | ';

/**
 * If the title is already multi-segment, this returns the last segment,
 * which is usually `t("common.application_name")`, but could also be a
 * custom value for customer with a custom page title setting enabled.
 */
const titleSuffix = () => {
  const currentTitle = window.document.title;
  const suffixIndex = currentTitle.lastIndexOf(SEPARATOR);
  if (suffixIndex > -1) {
    return currentTitle.substring(suffixIndex + SEPARATOR.length);
  }
  return currentTitle;
};

/**
 * Sets the page title, while being mindful of app name suffix.
 * @param {String | Array<String>} title
 * @example
 *   changeTitle('Settings');
 *     // <title>Settings | Lessonly</title>
 *   changeTitle(['History', 'Learn']);
 *     // <title>History | Learn | Lessonly</title>
 */
const changeTitle = title => {
  const prefix = Array.isArray(title) ? title.join(SEPARATOR) : title;
  const suffix = titleSuffix();
  window.document.title = [prefix, suffix].join(SEPARATOR);
};

/**
 * React hook that for stateless components to change the page title.
 * @param {String | Array<String>} title
 * @example
 *   const MyComponent = props => {
 *     usePageTitle(t('learn.sidebar.my_learning_content'));
 *     return <div>Component body</div>;
 *   };
 */
export function usePageTitle(title) {
  useEffect(() => changeTitle(title), [title]);
}

/**
 * React component that changes the page title when mounted.
 * @param {String | Array<String>} title
 * @example
 *   <PageTitle title={'Settings'} />
 *     // <title>Settings | Lessonly</title>
 *   <PageTitle title={['History', 'Learn']} />
 *     // <title>History | Learn | Lessonly</title>
 */
const PageTitle = ({ title }) => {
  usePageTitle(title);
  return null;
};

export default PageTitle;
